/**
 * 録ったばかりの商談記録（PRODUCT_DEV-1200）
 *
 * 手帳の時間割は、予定に付いていない商談記録を**商談の軽量索引**から出す。索引は保存のあとで
 * 追いつく作りなので、録ってすぐ手帳へ戻ると記録がまだ索引に無く、時間割に出ないことがある。
 * 予定に付いた記録は予定の取得が id を返すので待てる（`useMeetingLiteIndex` の `awaitIds`）が、
 * 予定なしで録った記録は手帳が id を知る手段が無い。そこで保存したときに id を覚えておき、
 * 手帳はそれも待つ。
 *
 * - sessionStorage（そのタブを開いているあいだだけ）に置く（`diaryDateMemory` と同じ）
 * - **覚えるのは少しのあいだだけ**（`TTL_MS`）。索引に載らないまま消された記録を待ち続けないため
 *   （待つ取り直しにも上限があるが、手帳を開くたびに上限まで回さない）
 */
const KEY = "unizo.diary.justRecorded";
/** 覚えておく時間。索引が追いつくのは数秒〜数十秒なので、十分に長くとる */
const TTL_MS = 10 * 60 * 1000;
/** 覚えておく件数。古いものから捨てる */
const LIMIT = 10;

interface Remembered {
  id: string;
  at: number;
}

const readAll = (): Remembered[] => {
  try {
    const raw = sessionStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed)
      ? parsed.filter(
          (r): r is Remembered =>
            Boolean(r) && typeof r.id === "string" && typeof r.at === "number",
        )
      : [];
  } catch {
    return [];
  }
};

/** 覚えている、録ったばかりの記録の id（古すぎるものは除く）。 */
export function readJustRecorded(now: number = Date.now()): string[] {
  return readAll()
    .filter((r) => now - r.at < TTL_MS)
    .map((r) => r.id);
}

/** 保存できた記録の id を覚える。 */
export function rememberJustRecorded(id: string, now: number = Date.now()): void {
  if (!id) return;
  try {
    const kept = readAll().filter((r) => r.id !== id && now - r.at < TTL_MS);
    const next = [...kept, { id, at: now }].slice(-LIMIT);
    sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // 覚えられないだけ（索引が追いつけば出る）
  }
}

/** 索引に載った記録を忘れる（もう待たなくてよい）。 */
export function forgetJustRecorded(ids: readonly string[]): void {
  if (ids.length === 0) return;
  try {
    const drop = new Set(ids);
    const all = readAll();
    const next = all.filter((r) => !drop.has(r.id));
    if (next.length !== all.length) sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // 忘れられないだけ（10 分で読まれなくなる）
  }
}

/** ログアウトのとき消す（人ごとのものなので次の人に残さない）。 */
export function clearJustRecorded(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // 消せないだけ
  }
}
